import {
  CalendarOutlined,
  DashboardOutlined,
  ProjectOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Result, Space, Spin, Tabs, Tag, Typography } from 'antd';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { MeContext, roleLabel, TEAM_LEAD } from '../../components/ResourcePlanning/shared';
import api from '../../services/resourcePlanning/ResourcePlanningService';
import AccessTab from './AccessTab';
import OverviewTab from './OverviewTab';
import PlanningTab from './PlanningTab';
import ProjectsTab from './ProjectsTab';
import TeamsTab from './TeamsTab';

const ResourcePlanning: React.FC = () => {
  const { data: me, isLoading, error } = useQuery({ queryKey: ['resourcePlanning', 'me'], queryFn: api.me });
  const [params, setParams] = useSearchParams();
  if (isLoading) return <Spin style={{ margin: 48 }} />;
  if (error || !me)
    return <Result status='error' title='Planificarea nu poate fi incarcata' subTitle={(error as Error)?.message} />;
  if (!me.installed)
    return (
      <Result
        status='warning'
        title='Planificarea necesita actualizare'
        subTitle='Contactati administratorul pentru actualizarea modulului.'
      />
    );
  if (!me.canAccess)
    return (
      <Result
        status='403'
        title='Fara acces'
        subTitle='Modulul este disponibil administratorilor, directorului tehnic, responsabililor de planificare si sefilor de echipa de proiect.'
      />
    );
  const tabs = [
    ...(me.canAccess
      ? [
          { key: 'overview', icon: <DashboardOutlined />, label: 'Privire de ansamblu', children: <OverviewTab /> },
          { key: 'planning', icon: <CalendarOutlined />, label: 'Planificare', children: <PlanningTab /> },
          { key: 'projects', icon: <ProjectOutlined />, label: 'Proiecte', children: <ProjectsTab /> },
          { key: 'teams', icon: <TeamOutlined />, label: 'Echipe', children: <TeamsTab /> },
        ]
      : []),
    ...(me.isAdmin
      ? [{ key: 'access', icon: <SafetyCertificateOutlined />, label: 'Acces', children: <AccessTab /> }]
      : []),
  ];
  const active = tabs.some((tab) => tab.key === params.get('tab')) ? params.get('tab')! : tabs[0].key;
  return (
    <MeContext.Provider value={me}>
      <div style={{ padding: 16 }}>
        <Space align='center'>
          <Typography.Title level={4}>Planificare resurse</Typography.Title>
          <Tag>{me.role ? roleLabel(me.role) : me.isAdmin ? 'Administrator ERP' : roleLabel(TEAM_LEAD)}</Tag>
        </Space>
        {!me.canPlan && (
          <Alert
            type='info'
            showIcon
            message={
              me.ledTeamIds.length
                ? 'Puteti modifica doar alocarile si programul membrilor echipei pe care o conduceti. Restul planificarii este doar pentru vizualizare.'
                : 'Nu conduceti momentan nicio echipa activa, deci planificarea este doar pentru vizualizare.'
            }
          />
        )}
        <Tabs
          activeKey={active}
          onChange={(key) =>
            setParams((current) => {
              const next = new URLSearchParams(current);
              next.set('tab', key);
              return next;
            })
          }
          items={tabs}
        />
      </div>
    </MeContext.Provider>
  );
};
export default ResourcePlanning;
